<template>
  <div class="scans-schedule-widget">
    <div v-if="loading" class="scans-schedule-widget__loading">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <div v-else-if="error" class="scans-schedule-widget__error">
      {{ error }}
    </div>

    <div v-else-if="heatmapData && dateRange" class="scans-schedule-widget__content">
      <scans-heatmap-chart
        :heatmap-data="heatmapData"
        :date-range="dateRange"
        :is3d-view="is3dView"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import { calculateProjectsStatistics } from '@/services/repository';
import ScansHeatmapChart from '@/components/charts/ScansHeatmapChart.vue';

export default {
  name: 'ScansScheduleWidget',
  components: {
    ScansHeatmapChart,
  },
  props: {
    is3dView: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      heatmapData: null,
      dateRange: null,
      loading: false,
      error: null,
    };
  },
  computed: {
    ...mapGetters(['domainFilter']),
    formattedDateRange() {
      if (!this.dateRange) return '';
      const start = moment(this.dateRange.start).format('MMM D, YYYY');
      const end = moment(this.dateRange.end).format('MMM D, YYYY');
      return `${start} - ${end}`;
    },
  },
  mounted() {
    this.fetchHeatmapData();
  },
  watch: {
    domainFilter() {
      this.fetchHeatmapData();
    },
  },
  methods: {
    async fetchHeatmapData() {
      this.loading = true;
      this.error = null;

      try {
        const filters = {
          domain: this.domainFilter || [],
        };

        const response = await calculateProjectsStatistics(filters);

        if (response && response.heatmap) {
          const { heatmapData, dateRange } = response.heatmap;
          this.heatmapData = heatmapData || {};
          this.dateRange = dateRange || this.generateDefaultDateRange();
        } else {
          // Show empty heatmap instead of error
          this.heatmapData = {};
          this.dateRange = this.generateDefaultDateRange();
        }
      } catch (err) {
        console.error('Failed to fetch heatmap data:', err);
        this.error = this.$t('dashboard.scans.schedule.error.fetch.failed');
      } finally {
        this.loading = false;
      }
    },
    generateDefaultDateRange() {
      const start = moment().subtract(2, 'months').startOf('month');
      const end = moment().add(2, 'months').endOf('month');
      return {
        start: start.format('YYYY-MM-DD'),
        end: end.format('YYYY-MM-DD'),
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.scans-schedule-widget {
  display: flex;
  flex-direction: column;
  background-color: var(--bg-widget);
  border-radius: $radius-md;
  padding: $space-5 $space-6;
  // A fixed height (min/max alone leave it indefinite) lets the chart's 100% height resolve, so the 3D view can fill it
  height: rem(215);

  &__header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: $space-4;
  }

  &__title {
    color: var(--text-secondary);
    font-family: $font-family-regular;
    font-size: $font-size-sm;
    letter-spacing: $letter-spacing-wide;
    line-height: $line-height-snug;
    text-transform: uppercase;
  }

  &__date-range {
    color: var(--text-secondary);
    font-family: $font-family-regular;
    font-size: $font-size-sm;
    letter-spacing: $letter-spacing-normal;
    line-height: $line-height-snug;
  }

  &__loading {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-grow: 1;
    min-height: rem(150);
  }

  &__error {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-grow: 1;
    color: var(--text-error, #f44336);
    font-family: $font-family-regular;
    font-size: $font-size-md;
    text-align: center;
    padding: $space-6;
  }

  &__no-data {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-grow: 1;
    color: var(--text-secondary);
    font-family: $font-family-regular;
    font-size: $font-size-md;
    text-align: center;
    padding: $space-6;
  }

  &__content {
    flex-grow: 1;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
</style>
